import Card from '@packages/ui/card/card.tsx';
import SecondaryButton from '@packages/ui/button/secondary-button.tsx';
import { useNavigate, useParams } from 'react-router-dom';
import { formatGraphQlError, validationErrors } from '@client/module/error/error.ts';
import FormLayout from '@packages/ui/form/form-layout.tsx';
import FormItem from '@packages/ui/form/form-item.tsx';
import TextInput from '@packages/ui/form/input/text-input.tsx';
import PrimaryButton from '@packages/ui/button/primary-button.tsx';
import MenuSectionSelection from '@client/page/management/menu/menu-section/menu-section-selection-input/menu-section-selection.tsx';
import CardHeader from '@packages/ui/card/card-header.tsx';
import CardContent from '@packages/ui/card/card-content.tsx';
import CardFooter from '@packages/ui/card/card-footer.tsx';
import { ContentLayout, FullPrimaryContent } from '@packages/ui/content-layout.tsx';
import { Helmet } from 'react-helmet-async';
import { ProductConfigurationType } from '@client/graphql/types/graphql.ts';
import ApplicationErrorView from '@client/module/error/application-error-view.tsx';
import { useTranslate } from '@tolgee/react';
import { Controller, useForm } from 'react-hook-form';
import { ApolloError } from '@apollo/client';
import { captureException } from '@sentry/browser';
import { useCompanyCurrency } from '@packages/core/company/focused-company-context.ts';
import { useNotificationCenter } from '@packages/ui/notification/notification-center.ts';
import useCreateServiceMenuProduct from '@client/page/management/appointment/service/service-product/service-product-create/logic/use-create-service-product.ts';
import { currencySymbol, normalizeCurrency } from '@packages/core/utils/currency-formatter.ts';

interface MenuProductForm {
  code?: string;
  title: string;
  description: string;
  sectionId: string | null;
  price: string;
  duration: string;
  teamIds: string[];
}

const DESCRIPTION_MAX_LENGTH = 200;

export default function ServiceProductCreate() {
  const serviceId = useParams().serviceId ?? '';
  const navigate = useNavigate();
  const { showAlert } = useNotificationCenter();
  const currency = useCompanyCurrency();
  const { t } = useTranslate();

  const [create, { loading, error }] = useCreateServiceMenuProduct();

  const {
    handleSubmit,
    setError,
    clearErrors,
    control,
    reset,
    getValues,
    setValue,
    formState: { errors },
    watch,
  } = useForm<MenuProductForm>({
    defaultValues: {
      code: undefined,
      title: '',
      description: '',
      sectionId: null,
      price: '',
      duration: '',
      teamIds: [],
    },
    mode: 'onChange',
    criteriaMode: 'all',
  });

  const onSubmit = async (data: MenuProductForm) => {
    try {
      const result = await create({
        variables: {
          menuID: serviceId,
          teamIds: data.teamIds,
          input: {
            duration: Number(data.duration),
            menuProduct: {
              code: data.code === '' ? undefined : data.code,
              title: data.title,
              description: data.description,
              enabled: true,
              sectionID: data.sectionId,
              configurations: [
                {
                  title: 'Base',
                  type: ProductConfigurationType.Base,
                  values: [
                    {
                      name: 'Base',
                      price: normalizeCurrency(data.price, currency),
                    },
                  ],
                },
              ],
            },
          },
        },
      });
      if (result.data?.createAppointmentMenuProduct) {
        showAlert({
          status: 'success',
          title: 'Success',
          message: 'New menu product has been created',
        });
        const sectionId = getValues('sectionId');
        const duration = getValues('duration');

        reset(undefined, { keepDefaultValues: true });
        setValue('sectionId', sectionId);
        setValue('duration', duration);
      }
    } catch (err) {
      if (err instanceof ApolloError) {
        const applicationErrors = formatGraphQlError(err.graphQLErrors);
        const validationError = validationErrors(applicationErrors);

        for (const field in validationError) {
          setError(
            field as keyof MenuProductForm,
            {
              type: 'server',
              message: t(validationError[field] ?? 'validation-error.common.alert'),
            },
            { shouldFocus: true }
          );
        }
      }
      captureException(err);
    }
  };

  // TODO: Hide for now
  // const handleTeam = (checked: boolean, id: string) => {
  //   if (checked) {
  //     setValue('teamIds', [id]);
  //     return;
  //   }
  //   setValue('teamIds', []);
  // };

  return (
    <>
      <div className="grid grid-cols-1  xl:grid-cols-7 w-full  gap-4">
        <ContentLayout className="xl:col-span-5">
          <Helmet>
            <title>{t('service.service-detail.product.create.helmet.title')}</title>
            <meta name="description" content="This page allows you to create a new product" />
          </Helmet>
          <FullPrimaryContent>
            <Card>
              <CardHeader
                title={t('service.service-detail.product.create.header', 'Create service product')}
                withBackButton={true}
                backTarget={'../..'}
              />
              <CardContent>
                <FormLayout>
                  <FormItem className="max-w-lg" title={''}>
                    <Controller
                      control={control}
                      name="sectionId"
                      render={({ field: { onChange, value } }) => (
                        <MenuSectionSelection menuId={serviceId} value={value} onChange={onChange} />
                      )}
                    />
                  </FormItem>

                  <FormItem
                    className="max-w-lg sm:col-span-3"
                    title={t('service.service-detail.product.create.form-input.name.code', 'Code')}
                  >
                    <Controller
                      control={control}
                      name="code"
                      render={({ field: { onChange, value } }) => (
                        <TextInput
                          data-testid="title-input"
                          type="text"
                          label="code"
                          name=""
                          placeholder={t('service.service-detail.product.create.form-input.code.placeholder', 'Code')}
                          value={value ?? ''}
                          error={errors.code?.message}
                          autoComplete={'off'}
                          onChange={onChange}
                        />
                      )}
                    />
                  </FormItem>

                  <FormItem
                    className="max-w-lg sm:col-span-3"
                    title={t('service.service-detail.product.create.form-input.name.title', 'Title')}
                  >
                    <Controller
                      rules={{
                        required: t(
                          'service.service-detail.product.create.form-input.name.validate.required',
                          'Title is required'
                        ),
                        minLength: {
                          value: 3,
                          message: t(
                            'service.service-detail.product.create.form-input.name.validate.min-length',
                            'Min length is 3'
                          ),
                        },
                        maxLength: {
                          value: 100,
                          message: t(
                            'service.service-detail.product.create.form-input.name.validate.max-length',
                            'Max length is 100'
                          ),
                        },
                        pattern: {
                          value: /^\S.*\S$/,
                          message: t(
                            'service.service-detail.product.create.form-input.name.validate.no-whitespace',
                            'Menu product title must not start or end with whitespace'
                          ),
                        },
                      }}
                      control={control}
                      name="title"
                      render={({ field: { onChange, value } }) => (
                        <TextInput
                          data-testid="title-input"
                          type="text"
                          label="title"
                          name=""
                          placeholder={t('service.service-detail.product.create.form-input.name.placeholder', 'Title')}
                          value={value}
                          error={errors.title?.message}
                          autoComplete={'off'}
                          onChange={onChange}
                        />
                      )}
                    />
                  </FormItem>

                  <FormItem
                    subtitle={`(${String(watch('description').length)}/${String(DESCRIPTION_MAX_LENGTH)})`}
                    className="max-w-lg"
                    title={t('service.service-detail.product.create.form-input.description.title', 'Description')}
                  >
                    <Controller
                      control={control}
                      name="description"
                      render={({ field: { onChange, value } }) => (
                        <textarea
                          className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring focus:ring-indigo-200 min-h-36"
                          placeholder={t(
                            'service.service-detail.product.create.form-input.description.placeholder',
                            'Description'
                          )}
                          value={value}
                          name=""
                          onChange={onChange}
                          maxLength={DESCRIPTION_MAX_LENGTH}
                        />
                      )}
                    />
                  </FormItem>

                  <div className="grid grid-cols-2 max-w-sm gap-4">
                    <FormItem
                      className="max-w-lg sm:col-span-1"
                      title={t('service.service-detail.product.create.form-input.price.title', 'Price')}
                    >
                      <Controller
                        control={control}
                        rules={{
                          required: t(
                            'service.service-detail.product.create.form-input.price.validate.required',
                            'Price is required'
                          ),
                        }}
                        name="price"
                        render={({ field: { onChange, value } }) => (
                          <TextInput
                            data-testid="code-input"
                            type="number"
                            label="code"
                            name=""
                            placeholder={t(
                              'service.service-detail.product.create.form-input.price.placeholder',
                              'Price'
                            )}
                            value={value}
                            error={errors.price?.message}
                            autoComplete={'off'}
                            onChange={onChange}
                            suffix={<span className="text-gray-500">{currencySymbol(currency)}</span>}
                          />
                        )}
                      />
                    </FormItem>
                    <FormItem
                      className="max-w-lg sm:col-span-1"
                      title={t('service.service-detail.product.create.form-input.duration.title', 'Duration')}
                    >
                      <Controller
                        control={control}
                        rules={{
                          required: t(
                            'service.service-detail.product.create.form-input.duration.validate.required',
                            'Duration is required'
                          ),
                        }}
                        name="duration"
                        render={({ field: { onChange, value } }) => (
                          <TextInput
                            data-testid=""
                            type="number"
                            label=""
                            name=""
                            placeholder={t(
                              'service.service-detail.product.create.form-input.duration.placeholder',
                              'Duration'
                            )}
                            value={value}
                            error={errors.duration?.message}
                            autoComplete={'off'}
                            onChange={onChange}
                            suffix={
                              <span className="text-gray-500">
                                {t('service.service-detail.product.create.form-input.duration.minutes', 'Minutes')}
                              </span>
                            }
                          />
                        )}
                      />
                    </FormItem>
                  </div>

                  <ApplicationErrorView error={error} />
                </FormLayout>
              </CardContent>

              <CardFooter>
                <div className="flex justify-center space-x-4 w-96">
                  <SecondaryButton
                    className="flex-1"
                    onClick={() => {
                      navigate('../..');
                    }}
                  >
                    {t('common.button.cancel', 'Cancel')}
                  </SecondaryButton>
                  <PrimaryButton
                    id="create-button"
                    data-testid="create-button"
                    className="w-32 flex-1"
                    onClick={() => {
                      clearErrors();
                      handleSubmit(onSubmit)().catch(captureException);
                    }}
                    loading={loading}
                  >
                    {t('common.button.create', 'Create')}
                  </PrimaryButton>
                </div>
              </CardFooter>
            </Card>
          </FullPrimaryContent>
        </ContentLayout>

        <div className="xl:col-span-2 xl:max-w-[450px]">
          {/*<ServiceProductTeam teamIds={watch('teamIds')} onClick={handleTeam} />*/}
        </div>
      </div>
    </>
  );
}
